import { ApiResponse, BaseApi } from "../base";

export interface ServiceMetric {
  id: string;
  serviceType:
    | "collection_delivery"
    | "site_service"
    | "warehouse"
    | "fabrication"
    | "packing";
  name: string;
  baseRate: number;
  minimumCharge: number;
  minimumHours?: number;
  unitType?: string;
  isActive: boolean;
  clientId?: string;
}

export interface PositionRate {
  id: string;
  name: string;
  hourlyRate: number;
  positionRef?: string;
  clientId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PackingMetric {
  id: string;
  packingType: string;
  baseRate: number | string;
  minimumCharge: number | string;
  maxLength?: number | string | null;
  maxWidth?: number | string | null;
  maxHeight?: number | string | null;
  isActive?: boolean;
  clientId?: string | null;
  estimatedMinutesPerItem?: number | string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CollectionDeliveryCalculation {
  vesselRate: number;
  staffCosts: Array<{
    staffRoleId: string;
    quantity: number;
  }>;
  travelTime: number;
  siteTime: number;
}

export interface SiteServiceCalculation {
  staffCosts: Array<{
    staffRoleId: string;
    quantity: number;
    minimumHours: number;
  }>;
  siteTime: number;
}

export interface WarehouseCalculation {
  serviceRate: number;
  minimumCharge: number;
  unitType: "cubic_foot" | "hourly";
  quantity: number;
}

export interface ServiceCalculationResult {
  serviceName: string;
  totalCost: number;
  calculationDetails: any;
}

export interface CreateServiceMetricDto {
  serviceType: ServiceMetric["serviceType"];
  name: string;
  baseRate: number;
  minimumCharge: number;
  minimumHours?: number;
  unitType?: string;
  isActive?: boolean;
  clientId?: string;
}

export interface CreatePackingMetricDto {
  packingType: string;
  baseRate: number;
  minimumCharge: number;
  maxLength?: number;
  maxWidth?: number;
  maxHeight?: number;
  isActive?: boolean;
  clientId?: string;
  estimatedMinutesPerItem?: number;
}

// Packing Metric Supplies types
export type PackingCalculationType = 'surface_area' | 'perimeter' | 'volume' | 'fixed';

export interface PackingMaterialSupply {
  id: string;
  name: string;
  unit?: string;
  cost?: number;
  billableRate?: number;
}

export interface PackingMetricSupply {
  id: string;
  packingMetricId: string;
  supplyId: string;
  calculationType: PackingCalculationType;
  quantityMultiplier: number;
  wasteFactor: number;
  supply?: PackingMaterialSupply;
}

export interface MaterialBreakdownItem {
  id: string;
  supplyId: string;
  supplyName: string;
  supplyUnit: string;
  supplyDimensions: string | null;
  supplyAreaSqFt: number;
  billableRatePerUnit: number;
  calculationType: PackingCalculationType;
  quantityMultiplier: number;
  wasteFactor: number;
  baseQuantity: number;
  quantityNeeded: number;
  unitLabel: string;
  pricePerSqFt: number;
  billableTotal: number;
}

export interface MaterialBreakdown {
  dimensions: {
    length: number;
    width: number;
    height: number;
    rawSurfaceAreaSqFt: number;
    bufferedLength: number;
    bufferedWidth: number;
    bufferedHeight: number;
    surfaceAreaSqFt: number;
    perimeterFt: number;
    volumeCuFt: number;
    wasteBufferPercent: number;
  };
  materials: MaterialBreakdownItem[];
  totalMaterialCost: number;
}

class ServiceMetricsApi extends BaseApi {
  // Service Metrics endpoints
  async getServiceMetrics(
    clientId?: string
  ): Promise<ApiResponse<ServiceMetric[]>> {
    const params = clientId ? { clientId } : {};
    return this.get("/service-metrics/metrics", { params });
  }

  async getServiceMetricById(id: string): Promise<ApiResponse<ServiceMetric>> {
    return this.get(`/service-metrics/metrics/${id}`);
  }

  async createServiceMetric(
    data: CreateServiceMetricDto
  ): Promise<ApiResponse<ServiceMetric>> {
    return this.post("/service-metrics/metrics", data);
  }

  async updateServiceMetric(
    id: string,
    data: Partial<CreateServiceMetricDto>
  ): Promise<ApiResponse<ServiceMetric>> {
    return this.put(`/service-metrics/metrics/${id}`, data);
  }

  async deleteServiceMetric(id: string): Promise<ApiResponse<void>> {
    return this.delete(`/service-metrics/metrics/${id}`);
  }

  // Packing Metrics endpoints
  async getPackingMetrics(
    clientId?: string
  ): Promise<ApiResponse<PackingMetric[]>> {
    const params = clientId ? { clientId } : {};
    return this.get("/service-metrics/packing", { params });
  }

  async createPackingMetric(
    data: CreatePackingMetricDto
  ): Promise<ApiResponse<PackingMetric>> {
    return this.post("/service-metrics/packing", data);
  }

  async updatePackingMetric(
    id: string,
    data: Partial<CreatePackingMetricDto>
  ): Promise<ApiResponse<PackingMetric>> {
    return this.put(`/service-metrics/packing/${id}`, data);
  }

  async deletePackingMetric(id: string): Promise<ApiResponse<void>> {
    return this.delete(`/service-metrics/packing/${id}`);
  }

  // Calculation endpoints
  async calculateCollectionDelivery(
    data: CollectionDeliveryCalculation,
    clientId?: string
  ): Promise<ApiResponse<ServiceCalculationResult>> {
    const params = clientId ? { clientId } : {};
    return this.post("/service-metrics/calculate/collection-delivery", data, {
      params,
    });
  }

  async calculateSiteService(
    data: SiteServiceCalculation,
    clientId?: string
  ): Promise<ApiResponse<ServiceCalculationResult>> {
    const params = clientId ? { clientId } : {};
    return this.post("/service-metrics/calculate/site-service", data, {
      params,
    });
  }

  async calculateWarehouse(
    data: WarehouseCalculation,
    clientId?: string
  ): Promise<ApiResponse<ServiceCalculationResult>> {
    const params = clientId ? { clientId } : {};
    return this.post("/service-metrics/calculate/warehouse", data, { params });
  }

  async estimatePackingCost(
    length: number,
    width: number,
    height: number,
    clientId?: string
  ): Promise<ApiResponse<PackingMetric[]>> {
    const params = { length, width, height, ...(clientId ? { clientId } : {}) };
    return this.get("/service-metrics/estimate-packing", { params });
  }

  // Packing Metric Supplies endpoints
  async getPackagingMaterials(): Promise<ApiResponse<PackingMaterialSupply[]>> {
    return this.get("/service-metrics/packaging-materials");
  }

  async getPackingMetricSupplies(
    packingMetricId: string
  ): Promise<ApiResponse<PackingMetricSupply[]>> {
    return this.get(`/service-metrics/packing/${packingMetricId}/supplies`);
  }

  async addPackingMetricSupply(
    packingMetricId: string,
    data: {
      supplyId: string;
      calculationType: PackingCalculationType;
      quantityMultiplier?: number;
      wasteFactor?: number;
    }
  ): Promise<ApiResponse<PackingMetricSupply>> {
    return this.post(`/service-metrics/packing/${packingMetricId}/supplies`, data);
  }

  async updatePackingMetricSupply(
    id: string,
    data: {
      calculationType?: PackingCalculationType;
      quantityMultiplier?: number;
      wasteFactor?: number;
    }
  ): Promise<ApiResponse<PackingMetricSupply>> {
    return this.put(`/service-metrics/packing-supplies/${id}`, data);
  }

  async removePackingMetricSupply(id: string): Promise<ApiResponse<void>> {
    return this.delete(`/service-metrics/packing-supplies/${id}`);
  }

  async getMaterialBreakdown(
    packingMetricId: string,
    length: number,
    width: number,
    height: number
  ): Promise<ApiResponse<MaterialBreakdown>> {
    const params = { length, width, height };
    return this.get(`/service-metrics/packing/${packingMetricId}/material-breakdown`, { params });
  }
}

export const serviceMetricsApi = new ServiceMetricsApi();
